import { useAccountProposals } from '@/hooks/account/use-account-proposals';
import { usePendingGroupProposals } from '@/hooks/admin/group/use-pending-group-proposals';
import { usePendingMemberProposals } from '@/hooks/admin/member/use-pending-member-proposals';
import { useActiveAdminChangeProposal } from '@/hooks/admin/organization/use-active-admin-change-proposal';
import { useDisasterRecoveryProposalList } from '@/hooks/admin/organization/use-disaster-recovery-proposal-list';
import { useGetPolicyProposal } from '@/hooks/admin/policy/use-get-policy-proposal';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useQueuedTransactions } from '@/hooks/transaction/use-queued-transactions';
import { SIGNABLE_POLL_INTERVAL_MS } from '@/components/execution/execution-polling';

/** Shows a dot for each section whose existing hook returns pending items. */
export function useSidebarProposalBadges(): Partial<Record<string, boolean>> {
  const { organization } = useMyOrganization();
  const isAdmin = Boolean(organization?.isAdmin);
  const options = {
    refreshInterval: SIGNABLE_POLL_INTERVAL_MS,
    revalidateOnFocus: true,
  };
  const { transactions } = useQueuedTransactions({
    ...options,
    enabled: Boolean(organization),
  });
  const { proposals: accounts } = useAccountProposals({
    ...options,
    // Account reads also serve onboarding; pause this sidebar consumer for non-admins.
    isPaused: () => !isAdmin,
  });
  const { proposals: groups } = usePendingGroupProposals(options);
  const { proposals: members } = usePendingMemberProposals(options);
  const { proposal: policy } = useGetPolicyProposal(options);
  const { activeProposal: admins } = useActiveAdminChangeProposal(options);
  const { data: recovery } = useDisasterRecoveryProposalList(options);

  return {
    '/transactions': transactions.length > 0,
    // The accounts hook also includes historical proposals.
    '/accounts':
      isAdmin && accounts.some(({ proposal }) => proposal.status === 'PENDING'),
    '/policies': isAdmin && Boolean(policy),
    '/groups': isAdmin && groups.length > 0,
    '/members': isAdmin && members.length > 0,
    '/settings/organization':
      isAdmin &&
      Boolean(
        admins ||
          recovery?.drInitiationProposal ||
          recovery?.drCancellationProposal ||
          recovery?.drFinalizationProposal
      ),
  };
}
